import React, { useState, useEffect } from 'react';
import { Users, Loader2 } from 'lucide-react';
import { projectsApi } from '../services/api';
import { useTeamsQuery } from '../hooks/queries';
import { EMPTY_ARRAY } from '../lib/empty';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalClose,
  ResponsiveModalBody,
  ResponsiveModalFooter,
} from './ui/responsive-modal';

const AddProjectToTeamModal = ({ show, onClose, project, onSuccess }) => {
  const { t } = useTranslation();
  const teamsQuery = useTeamsQuery({ enabled: !!show });
  const teams = teamsQuery.data || EMPTY_ARRAY;
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const shownError = error || (teamsQuery.isError ? t('add_to_team.load_failed') : '');

  // Pre-select the project's team, if it has one, each time the modal opens
  const projectTeamId = project?.team_id;
  useEffect(() => {
    if (show && projectTeamId) setSelectedTeamId(projectTeamId);
  }, [show, projectTeamId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTeamId) {
      setError(t('add_to_team.select_team_error'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await projectsApi.update(project.id, { team_id: selectedTeamId });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error adding project to team:', error);
      setError(error.response?.data?.detail || t('add_to_team.add_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromTeam = async () => {
    setLoading(true);
    setError('');

    try {
      await projectsApi.update(project.id, { team_id: null });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error removing project from team:', error);
      setError(error.response?.data?.detail || t('add_to_team.remove_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveModal open={show} onOpenChange={(open) => { if (!open) onClose(); }}>
      <ResponsiveModalContent
        className="bg-popover border-border md:rounded-lg"
        overlayClassName="bg-black/50"
      >
        <ResponsiveModalHeader className="p-4 border-border">
          <Users className="w-5 h-5 text-amber-500 shrink-0" />
          <ResponsiveModalTitle className="text-lg text-foreground">
            {t('add_to_team.title')}
          </ResponsiveModalTitle>
          <ResponsiveModalClose className="text-muted-foreground hover:bg-transparent hover:text-foreground" />
        </ResponsiveModalHeader>

        <form
          id="add-project-to-team-form"
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 min-h-0"
        >
          <ResponsiveModalBody className="p-4 space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                {t('add_to_team.project_label')}: <span className="text-foreground font-medium">{project?.name}</span>
              </p>
            </div>

            {shownError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
                {shownError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">
                {t('add_to_team.select_team')}
              </label>
              <select
                value={selectedTeamId}
                onChange={(e) => {
                  setSelectedTeamId(e.target.value);
                  setError('');
                }}
                className="w-full px-3 py-2 bg-accent border border-input rounded text-foreground focus:outline-none focus:border-amber-500"
                disabled={loading}
              >
                <option value="">{t('add_to_team.choose_team')}</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </ResponsiveModalBody>
        </form>

        <ResponsiveModalFooter className="flex gap-2 p-4 border-border bg-popover justify-stretch">
          <button
            type="submit"
            form="add-project-to-team-form"
            disabled={loading || !selectedTeamId}
            className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 disabled:cursor-not-allowed text-black rounded font-medium transition-colors flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {selectedTeamId === project?.team_id ? t('common.update') : t('add_to_team.add_to_team')}
          </button>

          {project?.team_id && (
            <button
              type="button"
              onClick={handleRemoveFromTeam}
              disabled={loading}
              className="px-4 py-2 bg-accent hover:bg-accent disabled:bg-accent/50 disabled:cursor-not-allowed text-foreground rounded font-medium transition-colors"
            >
              {t('common.remove')}
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 bg-accent hover:bg-accent disabled:bg-accent/50 disabled:cursor-not-allowed text-foreground rounded font-medium transition-colors"
          >
            {t('common.cancel')}
          </button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
};

export default AddProjectToTeamModal;
